import type { ListColumn } from './nullState'
import './list.css'

/**
 * Which columns the table shows, grouped as the list's data is. The first column is
 * always shown, so it is not offered; the column the list is sorted by is shown
 * whatever is chosen, so it is ticked and locked; a column the API cannot fill for any
 * row says why, in the list's words ("Not live yet").
 */
export function ColumnChooser<Row, Context>({
  columns,
  groupLabels,
  chosen,
  sorted,
  visibleCount,
  missing,
  onToggle,
  onReset,
}: {
  /** Every column the list can show, in display order. */
  columns: readonly ListColumn<Row, Context>[]
  /** Each group's legend, in the order the groups are offered. */
  groupLabels: Readonly<Record<string, string>>
  chosen: readonly string[]
  /** The column the list is sorted by. */
  sorted: ListColumn<Row, Context> | undefined
  visibleCount: number
  /** Why the API cannot fill this column for any loaded row ("Not live yet"), or null. */
  missing: (column: ListColumn<Row, Context>) => string | null
  onToggle: (key: string) => void
  onReset: () => void
}) {
  const [first] = columns
  const groups = Object.keys(groupLabels)
    .map((group) => ({ group, members: columns.filter((column) => column.group === group && column !== first) }))
    // A group holding only the always-shown first column has nothing to offer.
    .filter(({ members }) => members.length > 0)
  return (
    <details className="list-columns">
      <summary className="ci-button">Columns ({visibleCount})</summary>
      <div className="list-columns__panel" role="group" aria-label="Visible columns">
        {groups.map(({ group, members }) => (
          <fieldset key={group}>
            <legend>{groupLabels[group]}</legend>
            {members.map((column) => {
              const isSorted = column === sorted
              const why = missing(column)
              return (
                <label key={column.key}>
                  <input
                    type="checkbox"
                    checked={isSorted || chosen.includes(column.key)}
                    disabled={isSorted}
                    onChange={() => onToggle(column.key)}
                  />
                  {column.label}
                  {isSorted ? <span className="list-muted"> · sorted by</span> : null}
                  {why ? <span className="list-muted"> · {why}</span> : null}
                </label>
              )
            })}
          </fieldset>
        ))}
        <button type="button" className="ci-button" data-variant="ghost" onClick={onReset}>
          Reset to default
        </button>
      </div>
    </details>
  )
}
